import { useEffect, useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import { motion, useScroll, useSpring } from "framer-motion";
import { ArrowLeft, ArrowUpRight } from "lucide-react";
import { Nav } from "@/components/site/Nav";
import { Footer } from "@/components/site/Footer";
import { ShareButton } from "@/components/site/ShareButton";
import { Reveal, ease } from "@/components/site/Primitives";
import { PostCard } from "@/components/site/PostCard";
import { useLang, useT } from "@/lib/i18n";
import { fmtDate, getPost, posts } from "@/lib/posts";
import { useSeo } from "@/lib/seo";

const Block = ({ b }) => {
  if (b.h) return <h2 className="mt-14 font-display text-3xl md:text-4xl leading-tight">{b.h}</h2>;
  if (b.ul) return (
    <ul className="mt-6 space-y-3">
      {b.ul.map((li) => <li key={li} className="flex gap-3 text-stone-200"><span className="mt-2.5 w-1.5 h-1.5 rounded-full bg-[var(--gold)] shrink-0" />{li}</li>)}
    </ul>
  );
  if (b.quote) return (
    <blockquote className="my-14 border-l-2 border-[var(--gold)] pl-6 md:pl-8 font-display italic text-3xl md:text-4xl leading-snug text-[var(--gold)]">“{b.quote}”</blockquote>
  );
  return <p className="mt-6 text-base md:text-lg text-stone-300 leading-[1.8]">{b.p}</p>;
};

const NotFound = ({ t }) => (
  <div className="min-h-[70vh] grid place-items-center text-center px-5">
    <div>
      <p className="font-display text-5xl">{t.notFound}</p>
      <Link to="/blog" className="btn-ghost mt-8" data-testid="post-notfound-back">{t.back}</Link>
    </div>
  </div>
);

export default function PostPage() {
  const { slug } = useParams();
  const { lang } = useLang();
  const t = useT().blog;
  const post = getPost(slug);
  const { scrollYProgress } = useScroll();
  const progress = useSpring(scrollYProgress, { stiffness: 120, damping: 24 });

  useEffect(() => { window.scrollTo(0, 0); }, [slug]);

  const jsonLd = useMemo(() => post && ({
    "@context": "https://schema.org", "@type": "BlogPosting",
    headline: post.title[lang], description: post.excerpt[lang], image: post.cover,
    datePublished: post.date, inLanguage: lang, author: { "@type": "Person", name: "Lene Mauro" },
  }), [post, lang]);

  useSeo({
    title: post ? `${post.title[lang]} | Lene Mauro` : t.seoTitle,
    description: post ? post.excerpt[lang] : t.seoDesc,
    image: post?.cover,
    jsonLd,
  });

  const related = posts.filter((p) => p.slug !== slug).slice(0, 3);

  return (
    <div className="site" id="top">
      <div className="grain" aria-hidden="true" />
      <motion.div style={{ scaleX: progress }} className="fixed top-0 inset-x-0 h-[2px] bg-[var(--gold)] origin-left z-[60]" data-testid="post-progress" />
      <Nav />
      {!post ? <NotFound t={t} /> : (
        <main className="pt-28 md:pt-36">
          <article className="max-w-[1400px] mx-auto px-5 md:px-10" data-testid="post-article">
            <Link to="/blog" data-testid="post-back-link" className="inline-flex items-center gap-2 text-sm text-stone-300 hover:text-[var(--gold)] transition-colors">
              <ArrowLeft size={16} /> {t.back}
            </Link>
            <div className="mt-10 max-w-4xl">
              <p className="font-mono text-xs uppercase tracking-[0.2em] text-[var(--gold)]">{post.tag[lang]} · {fmtDate(post.date, lang)} · {post.readMin} min</p>
              <motion.h1 initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, ease }}
                className="mt-6 font-display font-light text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight" data-testid="post-title">
                {post.title[lang]}
              </motion.h1>
              <p className="mt-6 text-lg md:text-xl text-stone-300 leading-relaxed">{post.excerpt[lang]}</p>
            </div>
            <motion.div initial={{ clipPath: "inset(0 0 100% 0 round 28px)" }} animate={{ clipPath: "inset(0 0 0% 0 round 28px)" }} transition={{ duration: 1.3, delay: 0.2, ease }}
              className="mt-12 aspect-[16/9] md:aspect-[21/9] overflow-hidden rounded-[28px] bg-[#1c1917]">
              <img src={post.cover} alt={post.title[lang]} className="w-full h-full object-cover" data-testid="post-cover" />
            </motion.div>
            <div className="mt-8 md:mt-16 grid lg:grid-cols-12 gap-10">
              <aside className="lg:col-span-3 order-2 lg:order-1">
                <div className="lg:sticky lg:top-28 flex lg:flex-col items-center lg:items-start gap-4">
                  <img src="/lene.jpg" alt="Lene Mauro" className="w-14 h-14 rounded-full object-cover object-top" />
                  <div>
                    <p className="font-display text-xl">Lene Mauro</p>
                    <p className="font-mono text-[11px] uppercase tracking-[0.15em] text-stone-400">Social Media Manager</p>
                  </div>
                </div>
              </aside>
              <div className="lg:col-span-8 order-1 lg:order-2 max-w-3xl" data-testid="post-body">
                {post.body[lang].map((b, i) => <Block key={i} b={b} />)}
                <Reveal>
                  <div className="mt-16 rounded-[28px] border border-[var(--gold)]/40 bg-gradient-to-b from-[#2a2219] to-[#15120f] p-8 md:p-10">
                    <p className="font-display text-3xl md:text-4xl">{t.ctaTitle}</p>
                    <p className="mt-3 text-stone-300">{t.ctaSub}</p>
                    <div className="mt-8 flex flex-wrap gap-3">
                      <Link to="/" state={{ scrollTo: "contact" }} data-testid="post-cta-contact" className="btn-gold">{t.ctaContact} <ArrowUpRight size={18} /></Link>
                      <Link to="/" state={{ scrollTo: "gift" }} data-testid="post-cta-gift" className="btn-ghost">{t.ctaGift}</Link>
                    </div>
                  </div>
                </Reveal>
              </div>
            </div>
          </article>
          <section className="py-24 md:py-32">
            <div className="max-w-[1400px] mx-auto px-5 md:px-10">
              <h2 className="font-display font-light text-4xl sm:text-5xl tracking-tight">{t.related}</h2>
              <div className="mt-12 grid md:grid-cols-3 gap-6">
                {related.map((p) => <PostCard key={p.slug} post={p} />)}
              </div>
            </div>
          </section>
        </main>
      )}
      <Footer />
      <ShareButton />
    </div>
  );
}
